import { CalendarBookAppointmentIcon } from '@/components/atoms/icons';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import React from 'react';
import {
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';

export interface BookAppointmentButtonProps {
  onPress?: (() => void) | undefined;
  title?: string | undefined;
  disabled?: boolean | undefined;
  style?: StyleProp<ViewStyle> | undefined;
  textStyle?: StyleProp<TextStyle> | undefined;
  testID?: string | undefined;
}

export function BookAppointmentButton({
  onPress,
  title = 'Book Appointment',
  disabled = false,
  style,
  textStyle,
  testID,
}: Readonly<BookAppointmentButtonProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={title}
      disabled={disabled}
      onPress={onPress}
      testID={testID}
      style={({ pressed }) => {
        let opacity = 1;
        if (disabled) {
          opacity = 0.6;
        } else if (pressed) {
          opacity = 0.85;
        }
        return [
          styles.button,
          {
            backgroundColor: colors.buttonDefault || '#1A1A1A',
            opacity,
            // Icon stays on the left, text on the right, in both languages —
            // native RTL auto-mirrors plain 'row', so 'row-reverse' here
            // renders back as icon-left when isRTL is true.
            flexDirection: isRTL ? 'row-reverse' : 'row',
          },
          style,
        ];
      }}
    >
      <CalendarBookAppointmentIcon size={24} color={colors.backgroundLevel1 || '#FFFFFF'} />
      <View style={styles.textWrapper}>
        <Text
          style={[
            styles.text,
            { color: colors.backgroundLevel1 || '#FFFFFF' },
            textStyle,
          ]}
          numberOfLines={1}
          adjustsFontSizeToFit
        >
          {title}
        </Text>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 234,
    height: 52,
    paddingTop: 12, // Spacing/3
    paddingBottom: 12, // Spacing/3
    paddingLeft: 20, // Spacing/5
    paddingRight: 20, // Spacing/5
    gap: 12, // Spacing/3
    // Rounded corners face into the screen: the button sits flush against the
    // right edge in LTR and the left edge in RTL, so rounding is always on the
    // logical start side. Logical radii (not isRTL-swapped physical ones):
    // Android already swaps physical left/right radii under RTL, so a manual
    // swap double-flipped back to LTR there while iOS looked right.
    borderTopStartRadius: 16, // Spacing/4
    borderBottomStartRadius: 16, // Spacing/4
    borderTopEndRadius: 0, // Spacing/0
    borderBottomEndRadius: 0, // Spacing/0
  },
  textWrapper: {
    minWidth: 158,
    height: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  text: {
    fontFamily: 'Matter-SemiBold',
    fontSize: 16,
    fontWeight: '600',
    lineHeight: 22,
    letterSpacing: 0,
    textAlign: 'center',
  },
});

export default BookAppointmentButton;
